import { useEffect, useState } from "react";

import { BookOpenCheck, Megaphone, MessagesSquare } from "lucide-react";

import CoachesSlide from "@/../../hypetrain/static/img/exec-home/coaches-slide.png";
import LxpSlide from "@/../../hypetrain/static/img/exec-home/lxp-slide.webp";
import RoleplaySlide from "@/../../hypetrain/static/img/exec-home/roleplay-slide.webp";
import { Box } from "@/components/layout/flexbox";
import { Container } from "@/components/pieces/bootstrap";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { PUBLIC_CONTENT_MAX_WIDTH } from "@/css/constants";

import { AnimatedGraphic } from "./AnimatedGraphic";

function ExecLogosBg() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      fill="none"
      viewBox="0 0 1217 492"
    >
      <g fill="currentColor" clipPath="url(#clip0_42323_30826)">
        <path d="M0 318.385a193.5 193.5 0 0 0 26.679 37.112c71.978 78.672 194.105 84.098 272.776 12.119l86.886 94.965A324.4 324.4 0 0 1 349.057 492H0zM979.069 312.188c44.931-40.062 114.131-35.779 154.561 9.566s36.78 114.582-8.15 154.643c-44.94 40.062-114.14 35.778-154.567-9.567s-36.777-114.581 8.156-154.642M99.623 150.703c41.917-38.35 106.987-35.459 145.337 6.457 38.351 41.917 35.46 106.987-6.457 145.338s-106.986 35.46-145.337-6.457-35.46-106.987 6.457-145.338M823.182 140.352C934.184 41.384 1092.31 28.687 1216 97.346V274.75a210 210 0 0 0-11.84-14.539c-75.64-84.837-205.331-92.651-289.673-17.452zM38.798 82.72C22.84 97.322 9.896 113.987 0 131.933V40.315z"></path>
      </g>
      <defs>
        <clipPath id="clip0_42323_30826">
          <path fill="#fff" d="M.5 0h1216v492H.5z"></path>
        </clipPath>
      </defs>
    </svg>
  );
}

interface Slide {
  title: string;
  desc: string;
  activeTextColor: string;
  bgColor: string;
  logoColor: string;
  graphicSrc: string;
  mobileIcon: JSX.Element;
}

const SLIDES: Slide[] = [
  {
    title: "AI Roleplays",
    desc: "Practice high-stakes conversations with immediate, objective feedback",
    activeTextColor: "text-blue-700",
    bgColor: "bg-blue-200",
    logoColor: "#D6E3FE",
    graphicSrc: RoleplaySlide,
    mobileIcon: (
      <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-blue-300">
        <MessagesSquare className="text-blue-700" size={40} strokeWidth={1} />
      </div>
    ),
  },
  {
    title: "1:1 Coaching",
    desc: "Access proven leadership experts with custom coaching on your terms",
    activeTextColor: "text-yellow-700",
    bgColor: "bg-yellow-50",
    logoColor: "#FFF3D8",
    graphicSrc: CoachesSlide,
    mobileIcon: (
      <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-yellow-100">
        <Megaphone className="text-yellow-700" size={40} strokeWidth={1} />
      </div>
    ),
  },
  {
    title: "Learning Platform",
    desc: "Custom programs blend AI, coaching, and content with powerful analytics",
    activeTextColor: "text-green-700",
    bgColor: "bg-green-100",
    logoColor: "#CDE6CD",
    graphicSrc: LxpSlide,
    mobileIcon: (
      <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-green-200">
        <BookOpenCheck className="text-green-700" size={40} strokeWidth={1} />
      </div>
    ),
  },
];

interface FeatureSlideProps {
  slide: Slide;
  isActive: boolean;
  onClick: () => void;
}

function MobileFeatureSlide({ slide }: { slide: Slide }) {
  return (
    <div
      className={
        `mb-4 flex items-center gap-6 overflow-hidden rounded-xl p-4` +
        ` ${slide.bgColor}`
      }
    >
      {slide.mobileIcon}
      <div className="flex-1 text-left">
        <div
          className={`text-maintext text-xl font-semibold ${slide.activeTextColor}`}
        >
          {slide.title}
        </div>
        <div className="mt-2 text-sm">{slide.desc}</div>
      </div>
    </div>
  );
}

function DesktopFeatureSlide({ slide, isActive, onClick }: FeatureSlideProps) {
  return (
    <Clickable onClick={onClick}>
      <Box className="rounded-xl p-4 text-center hover:bg-white/40">
        <div
          className={`text-maintext ${isActive ? "opacity-100" : "opacity-50"}`}
        >
          <div
            className={
              `linear text-xl font-semibold transition-all duration-300 md:text-2xl ` +
              (isActive ? slide.activeTextColor : "")
            }
          >
            {slide.title}
          </div>
          <div className="mt-3">{slide.desc}</div>
        </div>
      </Box>
    </Clickable>
  );
}

export function FeatureHero() {
  const [slideIndex, setSlideIndex] = useState(0);
  const [autoProgress, setAutoProgress] = useState(true);

  // Auto-progress through slides
  useEffect(() => {
    if (autoProgress) {
      const interval = setInterval(() => {
        setSlideIndex((prevIndex) => (prevIndex + 1) % SLIDES.length);
      }, 7500);
      return () => clearInterval(interval);
    }
  }, [autoProgress]);

  const onSlideClick = (index: number) => {
    setAutoProgress(false);
    setSlideIndex(index);
  };

  return (
    <Container maxWidth={PUBLIC_CONTENT_MAX_WIDTH}>
      {/* Mobile layout - Stacked color blocks */}
      <div className="md:hidden">
        <div className="mb-6 flex flex-col">
          {SLIDES.map((slide, index) => (
            <MobileFeatureSlide key={index} slide={slide} />
          ))}
        </div>
      </div>

      {/* Desktop layout - Interactive tabs with animated graphic */}
      <div
        className={
          `linear relative hidden rounded-t-3xl px-10 pt-11 transition-all duration-300 md:block lg:px-14 xl:px-28 ` +
          ` ${SLIDES[slideIndex].bgColor}`
        }
      >
        <div className="z-20 mb-6 flex flex-row">
          {SLIDES.map((slide, index) => {
            const isActive = slideIndex === index;
            return (
              <DesktopFeatureSlide
                key={index}
                slide={slide}
                isActive={isActive}
                onClick={() => onSlideClick(index)}
              />
            );
          })}
        </div>
        <Box className="relative z-10 rounded-t-3xl">
          <AnimatedGraphic
            src={SLIDES[slideIndex].graphicSrc}
            slideIndex={slideIndex}
          />
        </Box>
        <div
          style={{ color: SLIDES[slideIndex].logoColor }}
          className="absolute right-0 bottom-0 left-0 z-0 w-full overflow-hidden"
        >
          <ExecLogosBg />
        </div>
      </div>
    </Container>
  );
}
